import React from 'react';
import styled from 'styled-components';
import { mediaLgMin, mediaXlMin } from 'helpers/breakpoints';
import NextImageWithShimmer from 'components/image/NextImageWithShimmer';

interface SpotlightSectionProps {
  id: string;
  textSide: 'left' | 'right';
  image: string;
  children: React.ReactNode;
}
const SpotlightSection = ({ id, textSide, image, children }: SpotlightSectionProps) => (
  <SectionWrapper id={id} reverseOrder={textSide === 'left'} data-testid={id}>
    <RatioBox>
      <NextImageWithShimmer alt="Own a boat" src={image} fill sizes="100vw" style={{ objectFit: 'cover' }} />
    </RatioBox>
    {/* The article is left intentionally blank, so the consumer of this component can add their own content with styling. */}
    <article>{children}</article>
  </SectionWrapper>
);

/**
 * Standard idiomatic CSS ordering:
 *  1. Positioning: position, top/right/bottom/left, z-index
 *  2. Box model: display, overflow, float, clear, box-sizing, width, min/max-width, height, min/max-height, margin, padding, border, border-radius
 *  3. Typography: font-*, text-*, color
 *  4. Background & visual: background, box-shadow, opacity, transform, transition
 *  5. Misc/animations
 *  6. Media queries at the bottom, ordered from narrowest to widest, or grouped per selector but consistent
 *
 * Child selector ordering:
 *  1. Element selectors (tags: h1, p, img)
 *  2. Utility classes (e.g. .imgDesktop, .imgMobile)
 *  3. Structural wrappers (e.g. .desktopWrap, .box)
 *  4. State selectors (pseudo-classes :hover, :focus)
 *  5. Media queries (nested inside each child or pulled out consistently)
 *
 * Rule of thumb:
 * - Flatten when possible. Each class or element should have a single, top-level definition.
 * - Nest only when there’s true coupling (e.g. .captain .box where .box really looks different in that state).
 * - Never nest just to express DOM structure—it bloats selectors and kills reuse.
 *
 * Media queries are ordered from narrowest to widest, or grouped per selector.
 */

// This sets the aspect ratio and gives a handle for control the Next Image
const RatioBox = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/2;

  @media ${mediaLgMin} {
    aspect-ratio: 16/9;
  }

  @media ${mediaLgMin} {
    aspect-ratio: 20/9;
  }

  @media ${mediaXlMin} {
    display: flex;
    height: 420px;
    width: 725px;
    aspect-ratio: unset;
  }
`;

const SectionWrapper = styled.section<{ reverseOrder: boolean }>`
  display: flex;

  flex-direction: column;
  margin: 100px 20px;
  border-radius: 10px;
  overflow: hidden;
  max-width: 1076px;

  @media ${mediaXlMin} {
    ${({ reverseOrder }) => (reverseOrder ? 'flex-direction: row-reverse;' : 'flex-direction: row;')}
    margin: 100px auto;
    width: fit-content;

    article {
      height: 420px;
      width: fit-content; // Hug content horizontally
      max-width: 100%;
      align-self: flex-start;
      flex-direction: column;
      justify-content: center;
      flex: 1 1 0;
    }
  }
`;

const makeStyledSpotlightWrapper = ({
  backgroundColor,
  textColor = 'white',
  linkColor,
}: {
  backgroundColor: string;
  textColor?: string;
  linkColor: string;
}) => styled.div`
  display: flex;

  flex-direction: column;
  justify-content: center;
  padding: 35px;
  height: 100%;
  width: 100%;
  background-color: ${backgroundColor};

  h2 {
    font-size: 32px;
    line-height: 36px;
    font-weight: bold;
    color: ${textColor};
  }

  p {
    margin-bottom: 16px;

    color: ${textColor};
  }

  a {
    font-weight: bold;
    color: ${linkColor};
    text-decoration: underline;
    text-decoration-color: ${linkColor}; // the color of the underline
  }

  // Screens wider than 1140px
  @media ${mediaXlMin} {
    margin: auto;
  }
`;
SpotlightSection.makeStyledSpotlightWrapper = makeStyledSpotlightWrapper;

export default SpotlightSection;
